import { useEffect, useRef, useState } from 'react';
import { FileSearch, Filter } from 'lucide-react';
import { chamarApi, ErroApi } from '../../../nucleo/auth/cliente-api';
import { PageHeader } from '../../../ui/PageHeader';
import { EmptyState } from '../../../ui/EmptyState';
import { Alert, AlertDescription } from '../../../ui/base/alert';
import { Button } from '../../../ui/base/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../ui/base/dialog';
import { Input } from '../../../ui/base/input';
import { Label } from '../../../ui/base/label';

const NS = '/bff/v1/j12-s01-selecionar-recorte';
type Dados = {
  periodoInicio: string;
  periodoFim: string;
  unidade: string;
  processo: 'PARAMETRIZACAO';
  nivelDetalhe: 'SINTETICO' | 'ANALITICO' | 'PESSOAL';
  finalidade?: string;
};
type Opcao = { id: string; nome: string };
type Estado = {
  titulo: string;
  descricao: string;
  identidade: { papeis: string[]; recorteGlobal: boolean };
  opcoes: { unidades: Opcao[]; processos: Opcao[]; niveis: Opcao[] };
  consultas: Array<{
    id: string;
    periodo_inicio: string;
    periodo_fim: string;
    unidade_efetiva: string;
    processo: string;
    nivel_detalhe: string;
    consulta_hash: string;
    created_at: string;
  }>;
  dialogo: {
    aberto: boolean;
    modo: 'FORMULARIO' | 'CONFIRMACAO' | null;
    dados: Dados | null;
    erros: Record<string, string>;
  };
  confirmacao: { necessaria: boolean; filtrosEfetivos: Record<string, unknown> | null };
  resultado: null | {
    tipo: string;
    mensagem: string;
    consultaId?: string;
    itens?: Array<Record<string, unknown>>;
    filtrosEfetivos?: Record<string, unknown>;
  };
  permissoes: { consultar: boolean };
};
type Envelope = {
  contract_version: string;
  screen: string;
  event: { verb: string; outcome: string; at: string };
  state: Estado;
};

export function SelecionarRecortePage() {
  const [estado, setEstado] = useState<Estado | null>(null),
    [erro, setErro] = useState<string | null>(null),
    [carregando, setCarregando] = useState(true),
    [enviando, setEnviando] = useState(false),
    chave = useRef<string | null>(null);
  async function chamar(verbo: string, corpo: unknown = {}) {
    return chamarApi<Envelope>(`${NS}/verbs/${verbo}`, { metodo: 'POST', corpo });
  }
  useEffect(() => {
    void chamarApi<Envelope>(`${NS}/state`)
      .then((r) => setEstado(r.state))
      .catch((e) => setErro(aviso(e)))
      .finally(() => setCarregando(false));
  }, []);
  async function preparar() {
    chave.current = null;
    setEstado((await chamar('preparar')).state);
  }
  async function validar() {
    if (!estado?.dialogo.dados) return;
    setEnviando(true);
    try {
      setEstado((await chamar('validar', estado.dialogo.dados)).state);
    } catch (e) {
      setErro(aviso(e));
    } finally {
      setEnviando(false);
    }
  }
  async function confirmar() {
    if (!estado?.dialogo.dados) return;
    chave.current ??= crypto.randomUUID();
    setEnviando(true);
    try {
      setEstado(
        (await chamar('confirmar', { ...estado.dialogo.dados, chaveIdempotencia: chave.current }))
          .state,
      );
    } catch (e) {
      setErro(aviso(e));
    } finally {
      setEnviando(false);
    }
  }
  async function fechar() {
    setEstado((await chamar('consultar_estado')).state);
  }
  function mudar(parcial: Partial<Dados>) {
    if (!estado?.dialogo.dados) return;
    setEstado({
      ...estado,
      dialogo: { ...estado.dialogo, dados: { ...estado.dialogo.dados, ...parcial } },
    });
  }
  if (carregando)
    return (
      <div role="status" className="py-16 text-center text-muted-foreground">
        Carregando relatórios…
      </div>
    );
  if (!estado)
    return (
      <EmptyState
        variant="error"
        title="Não foi possível carregar os relatórios"
        description={erro ?? 'Tente novamente.'}
      />
    );
  const d = estado.dialogo.dados,
    confirmando = estado.dialogo.modo === 'CONFIRMACAO';
  return (
    <section className="min-w-0 w-full max-w-[calc(100vw-2rem)] overflow-hidden">
      <PageHeader title={estado.titulo} description={estado.descricao}>
        <Button disabled={!estado.permissoes.consultar} onClick={() => void preparar()}>
          <Filter className="mr-2 h-4 w-4" />
          Selecionar recorte
        </Button>
      </PageHeader>
      {estado.resultado && (
        <Alert className="mb-4">
          <AlertDescription>{estado.resultado.mensagem}</AlertDescription>
        </Alert>
      )}
      {erro && (
        <Alert className="mb-4">
          <AlertDescription>{erro}</AlertDescription>
        </Alert>
      )}
      {estado.resultado?.itens && (
        <article className="mb-4 rounded-md border p-4">
          <h2 className="font-semibold">Resultado limitado</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Consulta {estado.resultado.consultaId} · {estado.resultado.itens.length} item(ns)
            projetado(s)
          </p>
          <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-md bg-muted p-3 text-xs">
            {JSON.stringify(estado.resultado.itens, null, 2)}
          </pre>
        </article>
      )}
      {estado.consultas.length === 0 ? (
        <EmptyState
          icon={FileSearch}
          title="Nenhuma consulta registrada"
          description="Selecione um recorte autorizado para iniciar a trilha reproduzível."
          actionLabel="Selecionar recorte"
          onAction={() => void preparar()}
        />
      ) : (
        <div className="grid gap-3">
          {estado.consultas.map((c) => (
            <article key={c.id} className="rounded-md border p-4">
              <h2 className="font-semibold">
                {c.processo} · {c.nivel_detalhe}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {data(c.periodo_inicio)} a {data(c.periodo_fim)} · {c.unidade_efetiva}
              </p>
              <p className="mt-2 break-all text-xs text-muted-foreground">
                Consulta {c.id} · hash {c.consulta_hash}
              </p>
            </article>
          ))}
        </div>
      )}
      <Dialog
        open={estado.dialogo.aberto}
        onOpenChange={(v) => {
          if (!v) void fechar();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmando ? 'Confirmar filtros efetivos' : 'Selecionar período e recorte'}
            </DialogTitle>
            <DialogDescription>
              {confirmando
                ? 'A consulta usará somente os filtros autorizados abaixo.'
                : 'Filtros fora da sua autorização serão recusados sem revelar contagens.'}
            </DialogDescription>
          </DialogHeader>
          {d &&
            (confirmando ? (
              <pre className="whitespace-pre-wrap break-all rounded-md bg-muted p-3 text-sm">
                {JSON.stringify(estado.resultado?.filtrosEfetivos, null, 2)}
              </pre>
            ) : (
              <div className="grid gap-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Campo
                    id="rel-inicio"
                    rotulo="Início"
                    tipo="date"
                    valor={d.periodoInicio}
                    erro={estado.dialogo.erros.periodoInicio}
                    mudar={(periodoInicio) => mudar({ periodoInicio })}
                  />
                  <Campo
                    id="rel-fim"
                    rotulo="Fim"
                    tipo="date"
                    valor={d.periodoFim}
                    erro={estado.dialogo.erros.periodoFim}
                    mudar={(periodoFim) => mudar({ periodoFim })}
                  />
                </div>
                <Selecao
                  id="rel-unidade"
                  rotulo="Unidade"
                  valor={d.unidade}
                  opcoes={estado.opcoes.unidades}
                  erro={estado.dialogo.erros.unidade}
                  mudar={(unidade) => mudar({ unidade })}
                />
                <Selecao
                  id="rel-processo"
                  rotulo="Processo"
                  valor={d.processo}
                  opcoes={estado.opcoes.processos}
                  mudar={(processo) => mudar({ processo: processo as Dados['processo'] })}
                />
                <Selecao
                  id="rel-detalhe"
                  rotulo="Nível de detalhe"
                  valor={d.nivelDetalhe}
                  opcoes={estado.opcoes.niveis}
                  mudar={(nivelDetalhe) =>
                    mudar({ nivelDetalhe: nivelDetalhe as Dados['nivelDetalhe'] })
                  }
                />
                {d.nivelDetalhe === 'PESSOAL' && (
                  <Campo
                    id="rel-finalidade"
                    rotulo="Finalidade do acesso"
                    valor={d.finalidade ?? ''}
                    erro={estado.dialogo.erros.finalidade}
                    mudar={(finalidade) => mudar({ finalidade })}
                  />
                )}
              </div>
            ))}
          <DialogFooter>
            <Button variant="outline" onClick={() => void fechar()}>
              Cancelar
            </Button>
            <Button
              disabled={enviando}
              onClick={() => (confirmando ? void confirmar() : void validar())}
            >
              {confirmando ? 'Executar consulta' : 'Revisar filtros'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
function Campo({
  id,
  rotulo,
  valor,
  mudar,
  erro,
  tipo = 'text',
}: {
  id: string;
  rotulo: string;
  valor: string;
  mudar: (v: string) => void;
  erro?: string;
  tipo?: string;
}) {
  return (
    <div>
      <Label htmlFor={id}>{rotulo}</Label>
      <Input
        id={id}
        type={tipo}
        value={valor}
        aria-invalid={!!erro}
        onChange={(e) => mudar(e.target.value)}
      />
      {erro && <p className="text-sm text-destructive">{erro}</p>}
    </div>
  );
}
function Selecao({
  id,
  rotulo,
  valor,
  opcoes,
  mudar,
  erro,
}: {
  id: string;
  rotulo: string;
  valor: string;
  opcoes: Opcao[];
  mudar: (v: string) => void;
  erro?: string;
}) {
  return (
    <div>
      <Label htmlFor={id}>{rotulo}</Label>
      <select
        id={id}
        value={valor}
        aria-invalid={!!erro}
        className="h-10 w-full rounded-md border border-input bg-background px-3 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
        onChange={(e) => mudar(e.target.value)}
      >
        {opcoes.map((o) => (
          <option key={o.id} value={o.id}>
            {o.nome}
          </option>
        ))}
      </select>
      {erro && <p className="text-sm text-destructive">{erro}</p>}
    </div>
  );
}
function data(v: string) {
  return new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' }).format(new Date(`${v}T00:00:00Z`));
}
function aviso(e: unknown) {
  return e instanceof ErroApi ? e.aviso : 'Não foi possível concluir. Tente novamente.';
}
